/**
 * Arrival Theme — Main JavaScript
 * Vicious Studio 2026
 *
 * Vanilla JS only. No jQuery, no frameworks.
 * Uses event delegation where practical.
 */

(function () {
  'use strict';

  /* ==========================================================================
     Mobile Menu Toggle
     ========================================================================== */

  function initMobileMenu() {
    var toggle = document.querySelector('.header__menu-toggle, [aria-controls="mobile-nav-drawer"]');
    var drawer = document.getElementById('mobile-nav-drawer');

    if (!toggle || !drawer) return;

    toggle.addEventListener('click', function () {
      var isOpen = toggle.getAttribute('aria-expanded') === 'true';
      var nowOpen = !isOpen;

      toggle.setAttribute('aria-expanded', String(nowOpen));
      toggle.setAttribute('aria-label', nowOpen ? 'Close menu' : 'Open menu');
      drawer.setAttribute('aria-hidden', String(!nowOpen));
      document.documentElement.classList.toggle('mobile-nav-open', nowOpen);

      // Scroll lock
      if (nowOpen) {
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = '';
      }
    });

    // Close drawer when clicking a link inside it
    drawer.addEventListener('click', function (e) {
      if (e.target.tagName === 'A') {
        toggle.setAttribute('aria-expanded', 'false');
        toggle.setAttribute('aria-label', 'Open menu');
        drawer.setAttribute('aria-hidden', 'true');
        document.documentElement.classList.remove('mobile-nav-open');
        document.body.style.overflow = '';
      }
    });

    // Close on Escape key
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && toggle.getAttribute('aria-expanded') === 'true') {
        toggle.setAttribute('aria-expanded', 'false');
        toggle.setAttribute('aria-label', 'Open menu');
        drawer.setAttribute('aria-hidden', 'true');
        document.documentElement.classList.remove('mobile-nav-open');
        document.body.style.overflow = '';
        toggle.focus();
      }
    });
  }

  /* ==========================================================================
     Cart Count Update
     ========================================================================== */

  function updateCartCount(count) {
    var badges = document.querySelectorAll('.header__cart-count, [data-cart-count]');
    badges.forEach(function (badge) {
      badge.textContent = count;
      if (count > 0) {
        badge.style.display = '';
        badge.setAttribute('aria-label', count + ' ' + (count === 1 ? 'item' : 'items'));
      } else {
        badge.style.display = 'none';
      }
    });
  }

  /**
   * Fetches the current cart state and updates the cart count badge.
   */
  function refreshCartCount() {
    fetch('/cart.js', {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' }
    })
      .then(function (response) { return response.json(); })
      .then(function (cart) {
        updateCartCount(cart.item_count);
      })
      .catch(function () {
        // Silently fail — badge just stays as-is
      });
  }

  /* ==========================================================================
     Quantity Selector (+/- buttons)
     ========================================================================== */

  function initQuantitySelectors() {
    // Use event delegation on the document for all quantity controls
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('.cart-item__quantity-btn, [data-quantity-btn]');
      if (!btn) return;

      var form = btn.closest('form');
      var input = form ? form.querySelector('.cart-item__quantity-input, [data-quantity-input]') : null;
      if (!input) return;

      var currentVal = parseInt(input.value, 10) || 1;
      var min = parseInt(input.getAttribute('min'), 10) || 0;
      var max = parseInt(input.getAttribute('max'), 10) || 99;
      var isMinus = btn.classList.contains('cart-item__quantity-btn--minus') || btn.hasAttribute('data-quantity-minus');
      var newVal;

      if (isMinus) {
        newVal = Math.max(min, currentVal - 1);
      } else {
        newVal = Math.min(max, currentVal + 1);
      }

      input.value = newVal;

      // If inside a cart item, auto-submit via AJAX
      var cartItem = btn.closest('.cart-item');
      if (cartItem) {
        e.preventDefault();
        var line = cartItem.getAttribute('data-line') || input.getAttribute('data-line');
        if (line) {
          fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ line: parseInt(line, 10), quantity: newVal })
          })
            .then(function (response) { return response.json(); })
            .then(function (cart) {
              updateCartCount(cart.item_count);
              // If quantity is 0, remove the cart item element
              if (newVal === 0 && cartItem) {
                cartItem.remove();
              }
              // Update line price if element exists
              var priceEl = cartItem.querySelector('.cart-item__line-price');
              if (priceEl && cart.items) {
                var updatedItem = cart.items[parseInt(line, 10) - 1];
                if (updatedItem) {
                  priceEl.textContent = formatMoney(updatedItem.final_line_price);
                }
              }
              // Update subtotal
              var subtotalEl = document.querySelector('.cart__subtotal-price');
              if (subtotalEl) {
                subtotalEl.textContent = formatMoney(cart.total_price);
              }
              // Reload if cart is empty
              if (cart.item_count === 0) {
                window.location.reload();
              }
            })
            .catch(function () {
              // Fallback: submit the form normally
              if (form) form.submit();
            });
        }
      }
    });

    // Handle manual input change
    document.addEventListener('change', function (e) {
      var input = e.target.closest('.cart-item__quantity-input, [data-quantity-input]');
      if (!input) return;

      var cartItem = input.closest('.cart-item');
      if (!cartItem) return;

      var line = cartItem.getAttribute('data-line') || input.getAttribute('data-line');
      var newVal = parseInt(input.value, 10) || 0;

      if (line) {
        fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ line: parseInt(line, 10), quantity: newVal })
        })
          .then(function (response) { return response.json(); })
          .then(function (cart) {
            updateCartCount(cart.item_count);
            var subtotalEl = document.querySelector('.cart__subtotal-price');
            if (subtotalEl) {
              subtotalEl.textContent = formatMoney(cart.total_price);
            }
            if (cart.item_count === 0) {
              window.location.reload();
            }
          })
          .catch(function () {});
      }
    });
  }

  /* ==========================================================================
     Format Money Helper
     ========================================================================== */

  function formatMoney(cents) {
    if (typeof cents === 'string') {
      cents = cents.replace('.', '');
    }
    var amount = (parseInt(cents, 10) / 100).toFixed(2);
    // Use the shop's money format if available, otherwise default to $
    if (window.Shopify && window.Shopify.money_format) {
      return window.Shopify.money_format.replace(/\{\{\s*amount\s*\}\}/, amount);
    }
    return '$' + amount;
  }

  /* ==========================================================================
     Smooth Scroll for Anchor Links
     ========================================================================== */

  function initSmoothScroll() {
    document.addEventListener('click', function (e) {
      var link = e.target.closest('a[href^="#"]:not([href="#"])');
      if (!link) return;

      var targetId = link.getAttribute('href').substring(1);
      var target = document.getElementById(targetId);

      if (target) {
        e.preventDefault();
        target.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });

        // Update URL hash without jumping
        if (history.pushState) {
          history.pushState(null, null, '#' + targetId);
        }
      }
    });
  }

  /* ==========================================================================
     Announcement Bar Dismiss
     ========================================================================== */

  function initAnnouncementBar() {
    var closeBtn = document.querySelector('.announcement-bar__close');
    if (!closeBtn) return;

    closeBtn.addEventListener('click', function () {
      var bar = closeBtn.closest('.announcement-bar');
      if (bar) {
        bar.style.display = 'none';
        // Remember dismissal for the session
        try {
          sessionStorage.setItem('arrival_announcement_dismissed', 'true');
        } catch (e) {
          // Storage not available — no-op
        }
      }
    });

    // Check if already dismissed this session
    try {
      if (sessionStorage.getItem('arrival_announcement_dismissed') === 'true') {
        var bar = document.querySelector('.announcement-bar');
        if (bar) {
          bar.style.display = 'none';
        }
      }
    } catch (e) {
      // Storage not available — no-op
    }
  }

  /* ==========================================================================
     Product Variant Selector
     ========================================================================== */

  function initVariantSelector() {
    // Listen for changes on any variant selector element
    document.addEventListener('change', function (e) {
      var selector = e.target.closest('[data-variant-selector]');
      if (!selector) return;

      var form = selector.closest('form');
      if (!form) return;

      // Gather all option selections
      var selectors = form.querySelectorAll('[data-variant-selector]');
      var selectedOptions = [];
      selectors.forEach(function (sel) {
        selectedOptions.push(sel.value);
      });

      // Find the matching variant from product JSON data
      var productDataEl = form.closest('[data-product-json]') || document.querySelector('[data-product-json]');
      if (!productDataEl) return;

      var productData;
      try {
        productData = JSON.parse(productDataEl.textContent || productDataEl.getAttribute('data-product-json'));
      } catch (err) {
        return;
      }

      var variants = productData.variants || [];
      var matchedVariant = null;

      for (var i = 0; i < variants.length; i++) {
        var variant = variants[i];
        var match = true;
        for (var j = 0; j < selectedOptions.length; j++) {
          if (variant.options[j] !== selectedOptions[j]) {
            match = false;
            break;
          }
        }
        if (match) {
          matchedVariant = variant;
          break;
        }
      }

      if (!matchedVariant) return;

      // Update hidden variant_id input
      var variantInput = form.querySelector('input[name="id"]');
      if (variantInput) {
        variantInput.value = matchedVariant.id;
      }

      // Update price display
      var priceEl = form.closest('.product') ? form.closest('.product').querySelector('[data-product-price]') : document.querySelector('[data-product-price]');
      var comparePriceEl = form.closest('.product') ? form.closest('.product').querySelector('[data-compare-price]') : document.querySelector('[data-compare-price]');

      if (priceEl) {
        priceEl.textContent = formatMoney(matchedVariant.price);
      }

      if (comparePriceEl) {
        if (matchedVariant.compare_at_price && matchedVariant.compare_at_price > matchedVariant.price) {
          comparePriceEl.textContent = formatMoney(matchedVariant.compare_at_price);
          comparePriceEl.style.display = '';
        } else {
          comparePriceEl.textContent = '';
          comparePriceEl.style.display = 'none';
        }
      }

      // Update URL with variant id
      if (history.replaceState) {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', matchedVariant.id);
        history.replaceState(null, '', url.toString());
      }

      // Disable add-to-cart if variant is not available
      var addBtn = form.querySelector('[data-add-to-cart]');
      if (addBtn) {
        if (matchedVariant.available) {
          addBtn.disabled = false;
          addBtn.textContent = addBtn.getAttribute('data-add-text') || 'Add to Cart';
        } else {
          addBtn.disabled = true;
          addBtn.textContent = 'Sold Out';
        }
      }
    });
  }

  /* ==========================================================================
     Sticky Header Scroll Effect
     ========================================================================== */

  function initStickyHeader() {
    var header = document.querySelector('.header--sticky');
    if (!header) return;

    var scrollThreshold = 10;

    function onScroll() {
      if (window.scrollY > scrollThreshold) {
        header.classList.add('header--scrolled');
      } else {
        header.classList.remove('header--scrolled');
      }
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  /* ==========================================================================
     Initialise everything on DOMContentLoaded
     ========================================================================== */

  document.addEventListener('DOMContentLoaded', function () {
    initMobileMenu();
    initQuantitySelectors();
    initSmoothScroll();
    initAnnouncementBar();
    initVariantSelector();
    initStickyHeader();
  });

})();
